// =====================================================================
// Assessment Center — Grading domain: orchestration.
// Applies grading-engine's pure rules against attempts-store's data.
// This is the single place "what happens on submit" and "what happens
// after a review" are decided — both the student submit flow and the
// instructor review flow call into this file rather than duplicating
// the rules.
// =====================================================================
import {
  gradeAnswer,
  statusAfterSubmission,
  allEssaysReviewed,
  computeFinalGrade,
  type GradableAnswer,
  type FinalGrade,
} from "./grading-engine";
import * as store from "./attempts-store";
import type { ExamAttemptStatus } from "../types/db";

function allExamQuestionsFor(examId: string) {
  return store.getExamSections(examId).flatMap((s) => store.getExamQuestions(s.id));
}

export interface SubmissionGradingResult {
  status: ExamAttemptStatus;
  mc_points_earned: number;
  mc_points_possible: number;
  pending_essay_count: number;
}

/**
 * Runs immediately when a student submits an attempt.
 *   - Multiple Choice exam → every answer graded now, attempt → 'graded',
 *     final grade computed and stored immediately.
 *   - Essay / Mixed exam   → MC answers (if any) graded now; every essay
 *     answer gets a 'pending' essay_reviews row; attempt stays
 *     'submitted' (== Waiting for Review) until an instructor completes
 *     every pending review.
 */
export function gradeSubmission(attemptId: string): SubmissionGradingResult {
  const attempt = store.getAttempt(attemptId);
  if (!attempt) throw new Error("Attempt not found.");
  const exam = store.getExam(attempt.exam_id);
  if (!exam) throw new Error("Exam not found.");

  let mcEarned = 0;
  let mcPossible = 0;
  let pendingEssayCount = 0;

  for (const eq of allExamQuestionsFor(exam.id)) {
    const question = store.getQuestion(eq.question_id);
    if (!question) continue;
    const points = store.pointsForExamQuestion(eq);

    if (question.type === "multiple_choice") {
      mcPossible += points;
      const sa = store.getStudentAnswer(attemptId, eq.id);
      if (sa) {
        const input: GradableAnswer = {
          exam_question_id: eq.id,
          question_type: "multiple_choice",
          points_possible: points,
          selected_answer_id: sa.selected_answer_id,
          correct_answer_id: store.getCorrectAnswerId(question.id),
          essay_answer_text: null,
        };
        const graded = gradeAnswer(input);
        store.setAnswerGrade(sa.id, graded);
        mcEarned += graded.points_awarded ?? 0;
      }
      // No answer at all simply contributes 0 — nothing to write.
    } else {
      // Essay: guarantee a student_answer row exists (even blank) so a
      // reviewer always has something to look at, then queue a review.
      let sa = store.getStudentAnswer(attemptId, eq.id);
      if (!sa) {
        sa = store.upsertStudentAnswer({
          exam_attempt_id: attemptId,
          exam_question_id: eq.id,
          essay_answer_text: "",
        });
      }
      store.createPendingEssayReview(sa.id, exam.created_by);
      pendingEssayCount += 1;
    }
  }

  const status = statusAfterSubmission(exam.exam_type);
  store.updateAttempt(attemptId, { status, submitted_at: attempt.submitted_at ?? new Date().toISOString() });

  if (status === "graded") {
    // Multiple Choice exam — nothing pending, finalize the grade now.
    const final = computeFinalGrade({
      totalEarned: mcEarned,
      totalPossible: mcPossible,
      passingScore: exam.passing_score,
    });
    store.upsertGrade({
      exam_attempt_id: attemptId,
      student_id: attempt.student_id,
      total_score: final.total_score,
      max_score: final.max_score,
      percentage: final.percentage,
      passed: final.passed,
    });
  }

  return { status, mc_points_earned: mcEarned, mc_points_possible: mcPossible, pending_essay_count: pendingEssayCount };
}

/**
 * Called when an instructor completes one essay review. Scores that
 * single answer and marks the review 'completed'. Does NOT publish
 * the grade — publishing is now a separate, explicit instructor
 * action (see `publishGrade` below), so an instructor can review every
 * essay on an attempt and only release the result to the student when
 * they choose to.
 */
export function reviewEssayAnswer(
  reviewId: string,
  input: { score: number; feedback: string }
): { attemptId: string; readyToPublish: boolean } {
  const review = store.completeEssayReview(reviewId, input);
  const studentAnswer = store.getStudentAnswerById(review.student_answer_id);
  if (!studentAnswer) throw new Error("Answer not found for this review.");
  store.setAnswerGrade(studentAnswer.id, { is_correct: null, points_awarded: review.score });

  const attemptId = studentAnswer.exam_attempt_id;
  return { attemptId, readyToPublish: allEssaysReviewedForAttempt(attemptId) };
}

/** True once every essay on the attempt has a completed review — i.e. nothing left blocking `publishGrade`. */
export function allEssaysReviewedForAttempt(attemptId: string): boolean {
  const attempt = store.getAttempt(attemptId);
  if (!attempt) throw new Error("Attempt not found.");
  const exam = store.getExam(attempt.exam_id);
  if (!exam) throw new Error("Exam not found.");

  const essayEQs = allExamQuestionsFor(exam.id).filter(
    (eq) => store.getQuestion(eq.question_id)?.type === "essay"
  );
  const completedCount = store.listEssayReviews(attemptId).filter((r) => r.status === "completed").length;
  return allEssaysReviewed(essayEQs.length, completedCount);
}

/**
 * Explicit instructor action: computes the final grade from every
 * graded answer (MC points already known, essay points from completed
 * reviews) and publishes it — this is the moment the student's Results
 * page starts showing a score. Throws if any essay still lacks a
 * completed review, so it can't be called early by mistake.
 */
export function publishGrade(attemptId: string): FinalGrade {
  const attempt = store.getAttempt(attemptId);
  if (!attempt) throw new Error("Attempt not found.");
  const exam = store.getExam(attempt.exam_id);
  if (!exam) throw new Error("Exam not found.");

  if (attempt.status === "graded") {
    throw new Error("This attempt has already been graded.");
  }
  if (!allEssaysReviewedForAttempt(attemptId)) {
    throw new Error("Every essay answer needs a completed review before publishing.");
  }

  const allEQs = allExamQuestionsFor(exam.id);
  let totalEarned = 0;
  let totalPossible = 0;
  for (const eq of allEQs) {
    totalPossible += store.pointsForExamQuestion(eq);
    const sa = store.getStudentAnswer(attemptId, eq.id);
    totalEarned += sa?.points_awarded ?? 0;
  }

  const final = computeFinalGrade({ totalEarned, totalPossible, passingScore: exam.passing_score });
  store.upsertGrade({
    exam_attempt_id: attemptId,
    student_id: attempt.student_id,
    total_score: final.total_score,
    max_score: final.max_score,
    percentage: final.percentage,
    passed: final.passed,
  });
  store.updateAttempt(attemptId, { status: "graded" });
  return final;
}

// ---------------------------------------------------------------------
// Demo seed data — run through the real grading pipeline (not hand-
// computed) so the Grading queue and Results pages have something to
// show on first load, and so the seed itself exercises the same code
// path as a live submission. Runs once, when this module is first
// imported (module evaluation is cached — safe to import from
// multiple places).
// ---------------------------------------------------------------------
(function seedDemoAttempts() {
  const daysAgo = (n: number) => new Date(Date.now() - n * 24 * 60 * 60 * 1000).toISOString();

  // Multiple Choice exam (exam_2) — both answers correct, grades immediately.
  const mcAttempt = store.insertAttempt({
    id: "attempt_seed_mc",
    exam_id: "exam_2",
    student_id: "student_1",
    attempt_number: 1,
    status: "in_progress",
    started_at: daysAgo(2),
    submitted_at: null,
    created_at: daysAgo(2),
  });
  store.upsertStudentAnswer({ exam_attempt_id: mcAttempt.id, exam_question_id: "eq_4", selected_answer_id: "a_8" });
  store.upsertStudentAnswer({ exam_attempt_id: mcAttempt.id, exam_question_id: "eq_5", selected_answer_id: "a_12" });
  gradeSubmission(mcAttempt.id);
  store.updateAttempt(mcAttempt.id, { submitted_at: daysAgo(2) });

  // Mixed exam (exam_1) — MC graded now, essay left pending for the
  // instructor Grading queue to demonstrate.
  const mixedAttempt = store.insertAttempt({
    id: "attempt_seed_mixed",
    exam_id: "exam_1",
    student_id: "student_1",
    attempt_number: 1,
    status: "in_progress",
    started_at: daysAgo(1),
    submitted_at: null,
    created_at: daysAgo(1),
  });
  store.upsertStudentAnswer({ exam_attempt_id: mixedAttempt.id, exam_question_id: "eq_1", selected_answer_id: "a_2" }); // correct
  store.upsertStudentAnswer({ exam_attempt_id: mixedAttempt.id, exam_question_id: "eq_2", selected_answer_id: "a_5" }); // incorrect
  const essayAnswer = store.upsertStudentAnswer({
    exam_attempt_id: mixedAttempt.id,
    exam_question_id: "eq_3",
    essay_answer_text:
      "Controlled airspace requires an ATC clearance before entry and ATC provides active separation between " +
      "traffic; uncontrolled airspace has no such clearance requirement and pilots are responsible for their own " +
      "separation using see-and-avoid. See attached diagram and reference notes.",
  });
  // Demo attachments (migration 0002) — real sample files ship under
  // /public/sample-attachments so "open image" / "open PDF" is a
  // working demo, not a dead link.
  store.addAttachment({
    student_answer_id: essayAnswer.id,
    file_type: "image",
    file_name: "airspace-diagram.png",
    file_url: "/sample-attachments/airspace-diagram.png",
  });
  store.addAttachment({
    student_answer_id: essayAnswer.id,
    file_type: "pdf",
    file_name: "separation-minima-notes.pdf",
    file_url: "/sample-attachments/separation-minima-notes.pdf",
  });
  gradeSubmission(mixedAttempt.id);
  store.updateAttempt(mixedAttempt.id, { submitted_at: daysAgo(1) });
})();
